import React, { useRef, useEffect, useImperativeHandle } from "react";
import gsap from "gsap";
import { createTallyLibrary } from "../../lib/TallyLibrary";
import {
  TallyAnimationProps,
  ParticleFunctionType,
} from "../../lib/TallyLibrary/types";
import SETTINGS from "../../lib/TallyLibrary/constants/configGeneral.json";
import { mapRange } from "../../lib/TallyLibrary/helpers";
import AnimationManager from "../../lib/AnimationManager";

function OuterCirclePanelAnimation({
  width,
  height,
  particleAmount,
  scale,
  blobSpeed,
  isResponsive,
  responsiveFactor,
  timelineRef,
  startNowRef,
}: TallyAnimationProps) {
  let particles: ParticleFunctionType[] = [];
  const settings = {
    width: width || SETTINGS.width,
    height: height || SETTINGS.height,
    blobSpeed: blobSpeed || SETTINGS.blobSpeed,
    particleAmount: particleAmount || SETTINGS.particleAmount,
    scale: scale || SETTINGS.scale,
    isResponsive: isResponsive || SETTINGS.isResponsive,
    responsiveFactor: responsiveFactor || SETTINGS.responsiveFactor,
  };
  const tl = gsap.timeline();
  const delayedCallRef = useRef<GSAPTween | null>(null);
  const refCanvas = useRef<HTMLCanvasElement>(null);
  const particleLibrary = useRef<ReturnType<typeof createTallyLibrary> | null>(
    null,
  );
  const canvasContextRef = useRef<CanvasRenderingContext2D | null>(null);
  const animationManager = AnimationManager.getInstance();
  const systemId = "outer-circle-animation";

  useImperativeHandle(startNowRef, () => ({
    triggerAnimationNow: (delay: number = 1) => {
      // stop previous delayed call
      if (delayedCallRef?.current) {
        delayedCallRef.current.progress(1, true); // trigger immediately
        delayedCallRef.current.kill(); // remove it
        delayedCallRef.current = null;
      }

      // start a new delayed call after `delay` seconds
      delayedCallRef.current = gsap.delayedCall(delay, () => {
        tl.to(refCanvas.current, { autoAlpha: 1 });
        particleLibrary.current?.startPersistentCircleAnimation();
        // Activate the animation system
        animationManager.setSystemActive(systemId, true);
      });
    },
  }));

  useEffect(() => {
    if (refCanvas.current && !particleLibrary.current) {
      // Cache canvas context to avoid repeated getContext calls
      canvasContextRef.current = refCanvas.current.getContext("2d");

      particleLibrary.current = createTallyLibrary({
        canvas: refCanvas.current,
        config: {
          dotColor: "brown",
          includeMagnitation: false,
          blobStrength: 25, // Enable blob effects
          blobMultiplier: 0.0008, // Enable blob multiplier
          blobSpeed: settings.blobSpeed, // Pass the component blobSpeed to TallyLibrary
          vangleMultiplier: 0.1, // Slow down individual particle rotation (10x slower)
        },
      });

      // 9.6 is the animation timeframe defined in CirclePanel
      gsap.delayedCall(9.6, () => {
        tl.to(refCanvas.current, { autoAlpha: 1 });
        particleLibrary.current?.startPersistentCircleAnimation();
        // Activate the animation system
        animationManager.setSystemActive(systemId, true);
      });
      if (timelineRef) {
        timelineRef.current = tl;
      }

      if (settings.isResponsive) {
        resizeHandler();
        window.addEventListener("resize", resizeHandler);
      }
    }

    return () => {
      // Unregister from animation manager
      animationManager.unregisterParticleSystem(systemId);

      if (settings.isResponsive) {
        window.removeEventListener("resize", resizeHandler);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      particleLibrary.current &&
      refCanvas.current &&
      canvasContextRef.current
    ) {
      setupParticlesWithManager();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [particleLibrary.current]);

  const setupParticlesWithManager = () => {
    if (
      particleLibrary.current &&
      refCanvas.current &&
      canvasContextRef.current
    ) {
      particles = [];
      for (let i = 0; i < settings.particleAmount; i++) {
        const particle = particleLibrary.current.createParticle("px", i);
        particles.push(particle);
      }

      const blobOffset = {
        x: 0,
        y: 0,
      };

      // Register with AnimationManager instead of creating local animation loop
      animationManager.registerParticleSystem({
        id: systemId,
        particles,
        blobOffset,
        settings,
        context: canvasContextRef.current,
        canvas: refCanvas.current,
        isActive: false, // Will be activated when animation starts
        mapRange,
      });
    }
  };

  const resizeHandler = () => {
    if (!refCanvas.current) return;

    const base = Math.min(window.innerWidth, window.innerHeight);
    const size = base * settings.responsiveFactor;

    refCanvas.current.style.width = size + "px";
    refCanvas.current.style.height = size + "px";

    particleLibrary.current?.update({ width: size, height: size });
  };

  return (
    <canvas
      ref={refCanvas}
      className="tally-canvas-circle-persistent"
      width={settings.width}
      height={settings.height}
    ></canvas>
  );
}

export default React.memo(OuterCirclePanelAnimation);
